import { useState } from "react";

import { AuthError, type AuthStep, completeNewPassword, respondMfa, signIn } from "@/lib/auth";
import { type AppUser } from "@/lib/types";

interface LoginPageProps {
  onLogin: (user: AppUser) => void;
  message?: string;
}

type Step = "credentials" | "mfa" | "newPassword";

export const LoginPage = ({ onLogin, message }: LoginPageProps) => {
  const [step, setStep] = useState<Step>("credentials");
  const [mfaLabel, setMfaLabel] = useState("SMS kód");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const handleStep = (stepResult: AuthStep) => {
    if (stepResult.kind === "success") {
      onLogin(stepResult.user);
      return;
    }
    if (stepResult.kind === "mfa") {
      setMfaLabel(stepResult.mfaType === "SOFTWARE_TOKEN_MFA" ? "Kód z autentizátoru" : "SMS kód");
      setStep("mfa");
      return;
    }
    if (stepResult.kind === "newPassword") {
      setStep("newPassword");
    }
  };

  const handleError = (err: unknown) => {
    if (err instanceof AuthError) {
      setError(czechErrorFor(err));
    } else {
      setError("Přihlášení se nezdařilo. Zkuste to prosím znovu.");
    }
  };

  const submitCredentials = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      handleStep(await signIn(email, password));
    } catch (err) {
      handleError(err);
    } finally {
      setBusy(false);
    }
  };

  const submitMfa = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      handleStep(await respondMfa(mfaCode));
    } catch (err) {
      handleError(err);
      setMfaCode("");
    } finally {
      setBusy(false);
    }
  };

  const submitNewPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      handleStep(await completeNewPassword(newPassword));
    } catch (err) {
      handleError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#1a2b6b]">
      <div className="w-full max-w-md rounded-2xl bg-white p-12 shadow-xl">
        <h1 className="mb-4 text-5xl tracking-tight text-[#1a2b6b]">
          <span className="font-bold">hlas</span>
          <span className="italic font-light">emmy</span>
        </h1>
        <p className="mb-8 text-base text-gray-500">
          Administrace — přihlaste se vaším Emmy účtem.
        </p>

        {message && (
          <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">{message}</div>
        )}
        {error && (
          <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error}</div>
        )}

        {step === "credentials" && (
          <form onSubmit={submitCredentials} className="flex flex-col gap-3">
            <label className="flex flex-col gap-1 text-sm text-gray-700">
              E-mail
              <input
                type="email"
                autoComplete="username"
                required
                disabled={busy}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="rounded-md border border-gray-300 p-2 text-base text-gray-900 focus:border-[#1a2b6b] focus:outline-none disabled:bg-gray-100"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm text-gray-700">
              Heslo
              <input
                type="password"
                autoComplete="current-password"
                required
                disabled={busy}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="rounded-md border border-gray-300 p-2 text-base text-gray-900 focus:border-[#1a2b6b] focus:outline-none disabled:bg-gray-100"
              />
            </label>
            <button
              type="submit"
              disabled={busy}
              className="mt-2 rounded-md bg-[#1a2b6b] p-2 text-base text-white hover:opacity-80 disabled:opacity-50"
            >
              {busy ? "Přihlašuji…" : "Přihlásit se"}
            </button>
          </form>
        )}

        {step === "mfa" && (
          <form onSubmit={submitMfa} className="flex flex-col gap-3">
            <p className="text-sm text-gray-500">
              {mfaLabel === "SMS kód"
                ? "Poslali jsme vám ověřovací kód SMS. Zadejte ho níže."
                : "Zadejte kód z vaší autentizátorové aplikace."}
            </p>
            <label className="flex flex-col gap-1 text-sm text-gray-700">
              {mfaLabel}
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                required
                disabled={busy}
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value)}
                className="rounded-md border border-gray-300 p-2 text-base text-gray-900 focus:border-[#1a2b6b] focus:outline-none disabled:bg-gray-100"
              />
            </label>
            <button
              type="submit"
              disabled={busy}
              className="mt-2 rounded-md bg-[#1a2b6b] p-2 text-base text-white hover:opacity-80 disabled:opacity-50"
            >
              {busy ? "Ověřuji…" : "Potvrdit kód"}
            </button>
          </form>
        )}

        {step === "newPassword" && (
          <form onSubmit={submitNewPassword} className="flex flex-col gap-3">
            <p className="text-sm text-gray-500">
              Tento účet je nový — před dokončením přihlášení je potřeba nastavit nové heslo.
            </p>
            <label className="flex flex-col gap-1 text-sm text-gray-700">
              Nové heslo
              <input
                type="password"
                autoComplete="new-password"
                required
                disabled={busy}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="rounded-md border border-gray-300 p-2 text-base text-gray-900 focus:border-[#1a2b6b] focus:outline-none disabled:bg-gray-100"
              />
            </label>
            <button
              type="submit"
              disabled={busy}
              className="mt-2 rounded-md bg-[#1a2b6b] p-2 text-base text-white hover:opacity-80 disabled:opacity-50"
            >
              {busy ? "Ukládám…" : "Nastavit heslo a pokračovat"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

const czechErrorFor = (err: AuthError): string => {
  switch (err.code) {
    case "NotAuthorizedException":
      return "Neplatný e-mail nebo heslo.";
    case "UserNotFoundException":
      return "Účet s tímto e-mailem neexistuje.";
    case "PasswordResetRequiredException":
      return "Účet vyžaduje reset hesla. Resetujte jej v Emmy administraci a zkuste to znovu.";
    case "UserNotConfirmedException":
      return "Účet není potvrzen. Potvrďte jej v Emmy administraci a zkuste to znovu.";
    case "CodeMismatchException":
      return "Zadaný kód je neplatný. Zkuste to znovu.";
    case "ExpiredCodeException":
      return "Kód vypršel. Začněte přihlášení znovu.";
    case "InvalidPasswordException":
      return "Heslo nesplňuje požadavky — zkuste delší a složitější.";
    case "LimitExceededException":
      return "Příliš mnoho pokusů. Zkuste to prosím za chvíli.";
    default:
      return `Přihlášení se nezdařilo (${err.code}).`;
  }
};
